import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  applyClubTheme,
  cacheBranding,
  readCachedBranding,
  type ClubBranding,
} from './applyClubTheme';
import { ThemeContext, type ColourScheme } from './ThemeContext';

const SCHEME_KEY = 'pb_scheme';

function initialScheme(): ColourScheme {
  const stored = localStorage.getItem(SCHEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Flash-free branding chain: localStorage cache → API → theme.css static defaults.
 * The anonymous /api/public/branding endpoint serves the safe subset before login.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<ClubBranding | null>(() => {
    const cached = readCachedBranding();
    if (cached) applyClubTheme(cached);
    return cached;
  });
  const [scheme, setScheme] = useState<ColourScheme>(initialScheme);

  useEffect(() => {
    document.documentElement.dataset.theme = scheme;
    localStorage.setItem(SCHEME_KEY, scheme);
  }, [scheme]);

  const refreshBranding = useCallback(async () => {
    try {
      const res = await fetch('/api/public/branding');
      if (!res.ok) return; // endpoint lands in a later phase — defaults are fine until then
      const fresh = (await res.json()) as ClubBranding;
      setBranding(fresh);
      applyClubTheme(fresh);
      cacheBranding(fresh);
    } catch {
      /* offline or API down — cached/default theme stays applied */
    }
  }, []);

  useEffect(() => {
    void refreshBranding();
  }, [refreshBranding]);

  const toggleScheme = useCallback(
    () => setScheme((s) => (s === 'light' ? 'dark' : 'light')),
    [],
  );

  return (
    <ThemeContext.Provider value={{ branding, scheme, toggleScheme, refreshBranding }}>
      {children}
    </ThemeContext.Provider>
  );
}
